import { forwardRef, useState } from 'react';
import useTranslations from './utils/language-provider';
import styled from 'styled-components';
import { BuildingType } from '../config/buildings';
import { keyframes } from 'styled-components';
import { ReactComponent as ArrowRight } from '../assets/arrow-right.svg';
import { hexToHsl } from '../config/theme';
import Breadcrumbs, { BreadcrumbsWrapper, ButtonsWrapper, StyledContactButton } from './ui/breadcrumbs';
import FullscreenToggleButton from './ui/fullscreen-button';
import { isMobileDevice } from './utils/mobile-detection';
import { ReactComponent as Contact } from '../assets/icon-contact.svg';

type KSBQuicklinksProps = {};

const slideUp = keyframes`
    from {
        transform: translateY(100%);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
`;
const QuickLinkBorder = styled.div``;

const ArrowIcon = styled.div`
    flex-shrink: 0;
    position: relative;
    right: 0;
    background: transparent !important;

    svg {
        position: relative;
        height: 10px;

        & path {
            fill: #fff;
        }
    }
`;
const BuildingWrapper = styled.div`
    z-index: 100;
    position: absolute;
    background: ${(props) => props.theme.colors.primary};
    width: 100%;
`;

const LinkList = styled.div`
    display: grid;
    grid-template-columns: repeat(6,1fr);
    width: 100%;
    height: 100%;
    max-height: 100vh;
    position: relative;
    overflow: hidden;
    aspect-ratio: 16/9;
`;

const SubLinksList = styled.div`
    position: absolute;
    bottom: calc(20% + 8px);
    left: 0;
    width: 100%;
    display: flex;
    flex-flow: column-reverse;
    gap: 8px;
    z-index: 200;

    @media ${(props) => props.theme.screens.lg} {
        bottom: calc(17% + 8px);
    }
`;

const SubLink = styled.a<{ color: string; $isInteractive: boolean }>`
    padding: 8px 10px;
    background: linear-gradient(90deg, ${(props) => props.color ?? '#000'}, rgba(255, 255, 255, 0) 100%);
    color: ${(props) => props.theme.colors.white};
    font-size: ${(props) => props.theme.typography.sm};
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: 0;
    transform: translateY(100%);
    will-change: transform, opacity;
    display: flex;
    justify-content: space-between;
    align-items: center;

    visibility: hidden;
    pointer-events: ${(props) => (props.$isInteractive ? 'auto' : 'none')};

    &:hover {
        background: ${(props) => props.color};
    }

    @media ${(props) => props.theme.screens.lg} {
        padding: 12px 15px;
        font-size: ${(props) => props.theme.typography.md};
    }
`;

const LinkBackground = styled.div<{ img: string }>`
    position: absolute;
    height: 100%;
    width: 100%;
    background: linear-gradient(0deg,${(props) => props.theme.colors.primary ?? '#000'}, rgba(255, 255, 255, 255) 100%);
    background-image: ${(props) => `url(` + props.img + `)`};
    background-repeat: no-repeat;
    background-size: cover;
    background-position: right;
    transition: transform 0.5s;
`;

const ListItem = styled.a<{ color: string; img: string }>`
    position: absolute;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: end;
    background: linear-gradient(0deg,${(props) => props.theme.colors.primary ?? '#000'}, rgba(255, 255, 255, 0) 75%);
    text-decoration: none;

    & ${QuickLinkBorder} {
        position: absolute;
        height: 10px;
        width: 100%;
        background: ${(props) => props.color};
        transition: height 0.5s;
    }

    & ${ArrowIcon} {
        display: inline;
        margin-left: 5px;
    }

    & h1 {
        z-index: 150;
        font-size: ${(props) => props.theme.typography.sm};
        font-weight: normal;
        color: ${(props) => props.theme.colors.white};
        padding: 0px 10px;
        margin: 0;
        height: 18%;
    }

    &:hover {
        & ${QuickLinkBorder} {
            height: 20%;
            background: linear-gradient(0deg,${(props) => hexToHsl(props.color) ?? '#000'}, ${(props) => (props.color ? props.color + 'FF' : '#000')} 100%);
        }
    }

    @media ${(props) => props.theme.screens.lg} {
        & h1 {
            font-size: ${(props) => props.theme.typography.lg};
            padding: 0px 12px;
            height: 16%
        }
        &:hover {
            & ${QuickLinkBorder} {
                height: 17%;
                background: linear-gradient(0deg,${(props) => hexToHsl(props.color) ?? '#000'}, ${(props) => (props.color ? props.color + 'FF' : '#000')} 100%);
        }
    }
}`;

const LinkContainer = styled.div<{ color: string }>`
    position: relative;
    height: 100%;
    align-items: end;
    overflow: hidden;
    cursor: pointer;

    &:hover ${SubLink} {
        animation: ${slideUp} 0.5s ease-out forwards;
        visibility: visible;
    }

    &:hover ${ListItem} div {
        height: 20%;
        background: linear-gradient(0deg,hsl(${(props) => {
            if (props.color) {
                const hslArray = hexToHsl(props.color);
                return `${hslArray[0]} ${hslArray[1]}% ${hslArray[2] > 10 ? hslArray[2] - 10 : 0}%`;
            }
        }}), ${(props) => props.color ?? '#000'} 100%);
    }

    &:hover ${LinkBackground} {
        transform: scale(1.05);
    }

    @media ${(props) => props.theme.screens.lg} {
        &:hover ${ListItem} div {
            height: 17%;
        }
    }
`;


const KSBQuicklinks = forwardRef<HTMLDivElement, KSBQuicklinksProps>((props, ref) => {
    const translations = useTranslations();
    const quicklinksConfig = translations.buildings[BuildingType.KSB_QUICKLINKS].quicklinks;
    const [interactiveSublinks, setInteractiveSublinks] = useState<Record<string, boolean>>({});

    const setSublinkInteractive = (sublinkKey: string, isInteractive: boolean) => {
        setInteractiveSublinks((currentState) => {
            if (isInteractive) {
                return { ...currentState, [sublinkKey]: true };
            }

            if (!currentState[sublinkKey]) {
                return currentState;
            }

            const nextState = { ...currentState };
            delete nextState[sublinkKey];
            return nextState;
        });
    };

    return (
        <BuildingWrapper ref={ref}>
            <LinkList>
                <BreadcrumbsWrapper>
                    <Breadcrumbs
                        building={{
                            label: translations.buildings[BuildingType.KSB_QUICKLINKS].name,
                            href: `/${BuildingType.KSB_QUICKLINKS}`,
                        }}
                    />
                    <ButtonsWrapper>
                        {isMobileDevice() && <FullscreenToggleButton />}
                        <StyledContactButton href={translations.contact?.href}>
                            <Contact />
                            <p>{translations.contact?.text || 'Kontakt'}</p>
                        </StyledContactButton>
                    </ButtonsWrapper>
                </BreadcrumbsWrapper>
                {Object.entries(quicklinksConfig).map(([key, config]) => (
                    <LinkContainer
                        key={key}
                        color={config.color}
                        onMouseLeave={() => {
                            config.sublinks?.forEach((link) => setSublinkInteractive(`${key}-${link.name}`, false));
                        }}
                    >
                        {config.sublinks && (
                            <SubLinksList>
                                {config.sublinks?.map((link, subIndex) => (
                                    <SubLink
                                        key={link.name}
                                        color={config.color}
                                        $isInteractive={Boolean(interactiveSublinks[`${key}-${link.name}`])}
                                        style={{
                                            animationDelay: `${100 + subIndex * 120}ms`,
                                        }}
                                        href={link.href}
                                        target='_blank'
                                        onAnimationEnd={() => setSublinkInteractive(`${key}-${link.name}`, true)}
                                    >
                                        {link.name}
                                        <ArrowIcon>
                                            <ArrowRight />
                                        </ArrowIcon>
                                    </SubLink>
                                ))}
                            </SubLinksList>
                        )}
                        <LinkBackground img={config.img} />
                        <ListItem color={config.color} img={config.img} href={config.href} target='_blank'>
                            <QuickLinkBorder />
                            <h1>
                                {config.name}
                                {!config.sublinks && (
                                    <ArrowIcon>
                                        <ArrowRight />
                                    </ArrowIcon>
                                )}
                            </h1>
                        </ListItem>
                    </LinkContainer>
                ))}
            </LinkList>
        </BuildingWrapper>
    );
});

export default KSBQuicklinks;
